/* eslint-disable react-hooks/rules-of-hooks */
'use client'

import { Fragment, useMemo, useState } from 'react'

import Flex from '@wiggui/ds/Flex'
import Button from '@wiggui/ds/Button'
import GoogleIcon from '@wiggui/ds/GoogleIcon'
import Input from '@wiggui/ds/Input'
import Modal from '@wiggui/ds/Modal'
import Spinner from '@wiggui/ds/Spinnner'
import Text from '@wiggui/ds/Text'
import { useReassignAdviser } from './hooks/useReassignAdviser'
import { createUrlMedia, composeClasses } from '@wiggot/utils'

const BrokerSelected = ({
  brokers
}: {
  brokers: {
    fullname: string
    photo?: string
  }[]
}) => {
  const getBrokerInitials = (name: string): string => {
    if (name) {
      const arrayName = name.split(' ')
      return `${arrayName[0]?.charAt(0) ?? ''} ${arrayName[1]?.charAt(0) ?? ''}`
    }
    return ''
  }

  const getPhotoBroker = (
    fullname: string,
    photo?: string,
    padding?: boolean
  ) => {
    if (photo) {
      return (
        <img
          className={`w-[32px] h-[32px] rounded-full ${
            padding && 'border-white border-[3px]'
          }`}
          alt={photo}
          src={createUrlMedia({ id: photo })}
        />
      )
    }
    return (
      <div
        className={`w-[32px] h-[32px] text-[10px] font-semibold rounded-full uppercase flex items-center justify-center bg-[#D5D8DE] ${
          padding && 'border-white border-[3px]'
        }`}
      >
        {getBrokerInitials(fullname)}
      </div>
    )
  }

  if (brokers.length === 1) {
    return (
      <div className="w-full flex items-center gap-2">
        {getPhotoBroker(brokers[0].fullname, brokers[0].photo)}
        <p>{brokers[0].fullname}</p>
      </div>
    )
  }

  const multiplePhotos = useMemo(() => {
    if (brokers.length > 2) {
      return [...brokers.slice(0, 2), { fullname: `${brokers.length - 2} +` }]
    }
    return brokers
  }, [brokers])

  return (
    <div className="w-full flex items-center gap-2">
      <div className="flex space-x-[-15px]">
        {multiplePhotos.map((item) => {
          return (
            <Fragment key={item.fullname}>
              {getPhotoBroker(item.fullname, item?.photo, true)}
            </Fragment>
          )
        })}
      </div>
      <p>Múltiples asesores</p>
    </div>
  )
}

export const ModalReassignAdviser = () => {
  const [show, setShow] = useState(false)
  const {
    adviserSelected,
    filteredOptions,
    handleChangeAdvisor,
    isLoading,
    modalReassignAdviser,
    nameAdviser,
    onClose,
    debounceNameAdviser,
    setNameAdviser,
    handleAssignedAdvisor
  } = useReassignAdviser()

  const getTextBroker = useMemo(() => {
    if (adviserSelected) {
      return <BrokerSelected brokers={[adviserSelected]} />
    }
    if (modalReassignAdviser?.properties?.length > 0) {
      const brokersArr = modalReassignAdviser?.properties
        .map((item) => ({
          fullname: item.brokerName,
          photo: item.brokerPhoto
        }))
        .filter(
          (obj, index, self) =>
            index ===
            self.findIndex((t) => obj.fullname && t.fullname === obj.fullname)
        )
      if (brokersArr.length > 0) {
        return <BrokerSelected brokers={brokersArr} />
      }
      return <Text className="text-base">Selecciona un asesor</Text>
    }
    return <Text className="text-base">Selecciona un asesor</Text>
  }, [modalReassignAdviser, adviserSelected])

  const calculateTitle = useMemo(() => {
    const allPropertiesSelectedIsPendingToAssign =
      modalReassignAdviser?.properties?.every(
        (property) => property?.pendingType === 'PENDING_TO_ASSIGN'
      )

    if (allPropertiesSelectedIsPendingToAssign) {
      return modalReassignAdviser?.properties?.length > 1
        ? 'Asignar propiedades'
        : 'Asignar propiedad'
    }

    return modalReassignAdviser?.properties?.length > 1
      ? 'Reasignar propiedades'
      : 'Reasignar propiedad'
  }, [modalReassignAdviser])

  if (!modalReassignAdviser?.isOpen) {
    return null
  }

  const getBrokerInitials = (name: string): string => {
    if (name) {
      const arrayName = name.split(' ')
      return `${arrayName[0]?.charAt(0) ?? ''} ${arrayName[1]?.charAt(0) ?? ''}`
    }
    return ''
  }

  return (
    <Modal
      className="w-full max-w-lg relative"
      enableBgBlur
      buttomSheetClassName="pb-5 relative"
      onClose={isLoading ? () => ({}) : onClose}
      isOpen={modalReassignAdviser?.isOpen}
    >
      <Modal.Header title={calculateTitle} isShowCloseButton isShowDivider />
      <Modal.Body className="p-6 w-full flex gap-6 flex-col items-center justify-center">
        <Text className="text-primary text-body-m w-full" align="left">
          Busca un asesor para asignarle{' '}
          {modalReassignAdviser?.properties?.length > 1
            ? 'estas propiedades'
            : 'esta propiedad'}
        </Text>
        <Flex
          dir="col"
          wFull
          gap="gap-3"
          items="center"
          justify="start"
          className="relative"
        >
          <button
            type="button"
            className="w-full h-[52px] border rounded-lg px-4 flex items-center justify-between"
            onClick={() => setShow((prev) => !prev)}
          >
            {getTextBroker}
            <GoogleIcon name="chevron_right" className="rotate-90" />
          </button>
          {show && (
            <div
              onMouseLeave={() => setShow(false)}
              className="md:absolute w-full flex flex-col gap-3 items-center justify-start bg-white  md:top-[62px] z-[1] p-4 border rounded-lg max-h-[235px] overflow-y-auto"
            >
              <Input
                name="name-adviser"
                value={nameAdviser}
                onChange={(event) => setNameAdviser(event?.target.value)}
                className="w-full"
                classNameContainer="w-full"
                classNameLabel="text-secondary"
                labelText="Escribe un nombre"
                leftIcon={
                  <GoogleIcon
                    name="search"
                    className="text-primary text-[24px]"
                  />
                }
              />
              {isLoading && (
                <Flex items="center" justify="center" wFull className="py-2">
                  <Spinner color="terciary" />
                </Flex>
              )}
              {!isLoading &&
                debounceNameAdviser?.length > 0 &&
                filteredOptions?.length === 0 && (
                  <Text
                    align="center"
                    className="text-body-bold-m text-primary"
                  >
                    No se encontraron resultados
                  </Text>
                )}
              {filteredOptions?.length > 0 &&
                filteredOptions?.map((item) => {
                  return (
                    <button
                      key={item?.userId + item?.fullname}
                      type="button"
                      onClick={(event: any) => {
                        setShow(false)
                        handleChangeAdvisor(item)
                        event.preventDefault()
                        event.stopPropagation()
                      }}
                      className={composeClasses(
                        adviserSelected?.fullname === item?.fullname &&
                          'font-bold',
                        'w-full text-left px-4 hover:font-semibold flex items-center gap-2 text-sm py-1'
                      )}
                    >
                      {item?.photo ? (
                        <img
                          className="w-[32px] h-[32px] rounded-full"
                          alt={item.fullname}
                          src={createUrlMedia({ id: item?.photo })}
                        />
                      ) : (
                        <div className="w-[32px] h-[32px] text-[10px] font-semibold rounded-full uppercase flex items-center justify-center bg-[#D5D8DE]">
                          {getBrokerInitials(item.fullname)}
                        </div>
                      )}
                      {item.fullname}
                    </button>
                  )
                })}
            </div>
          )}
        </Flex>
      </Modal.Body>
      <Modal.Footer
        className="w-full flex flex-row py-[9px] px-6 gap-2"
        isShowDivider
      >
        <Button
          variant="terciary"
          fullWidth
          onClick={isLoading ? () => ({}) : onClose}
        >
          Cancelar
        </Button>
        <Button
          variant="secondary"
          onClick={handleAssignedAdvisor}
          fullWidth
          isLoading={isLoading}
          isDisabled={isLoading || !adviserSelected}
        >
          Aceptar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
